import type { ClientMessage, ServerMessage, Uuid } from '@colplat/protocol';

/**
 * WebSocket transport with reconnect and backoff. A short-lived signed token
 * is fetched from `api` for each handshake attempt — the client never
 * asserts its own identity (ADR-0016: app id comes from the token).
 */

export type TransportState = 'connecting' | 'open' | 'closed';

export interface BackoffOptions {
  base: number;
  factor: number;
  max: number;
}

const DEFAULT_BACKOFF: BackoffOptions = { base: 500, factor: 2, max: 30_000 };

/**
 * Thrown by a `fetchToken` implementation to say *why* it could not produce a
 * token, which decides whether the transport keeps trying.
 *
 * The distinction is the difference between a working offline mode and a
 * broken one. `fetchToken` is an HTTP call, so while the network is down it
 * fails every time — and the transport used to count each of those as an
 * auth failure and give up permanently after three. A minute in a tunnel
 * therefore killed the connection for the rest of the page's life: no further
 * reconnect attempts, no sync when the signal returned, and an outbox that
 * would never flush.
 *
 * `fatal: true` means the server answered and refused us (401/403) — retrying
 * cannot help, and hammering an auth endpoint is exactly what the counter
 * exists to prevent. Anything else, including a plain rejection from `fetch`,
 * is transient and gets ordinary backoff.
 */
export class TokenFetchError extends Error {
  constructor(message: string, readonly fatal: boolean) {
    super(message);
    this.name = 'TokenFetchError';
  }
}

/** Exponential backoff with full jitter. Exported standalone so it's unit-testable without a socket. */
export function computeBackoff(
  attempt: number,
  opts: BackoffOptions = DEFAULT_BACKOFF,
  random: () => number = Math.random,
): number {
  const raw = Math.min(opts.max, opts.base * opts.factor ** attempt);
  return raw * (0.5 + random() * 0.5);
}

/**
 * What the browser will tell us about the network, injectable for tests.
 *
 * `navigator.onLine` is only trustworthy in one direction. `false` means the
 * OS has no network interface that could reach anything — definitive, and
 * worth acting on. `true` merely means an interface exists: a captive portal,
 * a VPN that dropped, or a server that is simply down all report `true`. So
 * this is used to skip work we know cannot succeed, never as evidence that
 * anything *will*.
 */
export interface ConnectivityLike {
  isOnline(): boolean;
  /** Fires on the browser's own `online`/`offline` events. */
  onChange(fn: (online: boolean) => void): () => void;
}

export function defaultConnectivity(): ConnectivityLike {
  const hasWindow = typeof window !== 'undefined' && typeof navigator !== 'undefined';
  return {
    isOnline: () => (hasWindow ? navigator.onLine : true),
    onChange: (fn) => {
      if (!hasWindow) return () => {};
      const online = (): void => fn(true);
      const offline = (): void => fn(false);
      window.addEventListener('online', online);
      window.addEventListener('offline', offline);
      return () => {
        window.removeEventListener('online', online);
        window.removeEventListener('offline', offline);
      };
    },
  };
}

export interface TransportOptions {
  wsUrl: () => string;
  fetchToken: () => Promise<{ token: string; expiresInSeconds: number }>;
  node: Uuid;
  schemaVersion: number;
  /** Injectable for tests. */
  webSocketCtor?: new (url: string, protocols?: string | string[]) => WebSocket;
  backoff?: BackoffOptions;
  random?: () => number;
  /**
   * Stop retrying entirely after this many consecutive *fatal* token-fetch
   * failures — the server actively refusing us. Transient failures (offline,
   * 5xx) never count towards this; see `TokenFetchError`.
   */
  maxAuthFailures?: number;
  /** Injectable for tests; defaults to `navigator.onLine` plus the window's online/offline events. */
  connectivity?: ConnectivityLike;
}

export class Transport {
  private ws: WebSocket | undefined;
  private _state: TransportState = 'closed';
  private attempt = 0;
  private authFailures = 0;
  private stopped = true;
  private intentionalClose = false;
  /** Guards against two overlapping connect attempts producing two sockets. */
  private connecting = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly connectivity: ConnectivityLike;
  private unsubscribeConnectivity: (() => void) | undefined;
  private readonly messageListeners = new Set<(msg: ServerMessage) => void>();
  private readonly stateListeners = new Set<(s: TransportState) => void>();
  private readonly schemaStaleListeners = new Set<(required: number) => void>();

  constructor(private readonly opts: TransportOptions) {
    this.connectivity = opts.connectivity ?? defaultConnectivity();
    this.watchConnectivity();
  }

  get state(): TransportState { return this._state; }

  private watchConnectivity(): void {
    if (this.unsubscribeConnectivity) return;
    this.unsubscribeConnectivity = this.connectivity.onChange((online) => this.onConnectivityChange(online));
  }

  /**
   * The browser's own network events, which are far better signals than a
   * timer. Losing the network used to be discovered only when a socket
   * eventually errored, and *regaining* it only when the next backoff tick
   * happened to fire — which, after a long outage, is up to 30 seconds of
   * sitting there connected-but-idle with an unsent outbox.
   *
   * On `online`, reset the backoff and retry immediately: the exponential
   * delay exists to avoid hammering an unreachable server, and we now have
   * positive evidence that premise no longer holds. On `offline`, stop
   * retrying until the browser says otherwise — every attempt in between is
   * guaranteed to fail, and each one costs a connection timeout.
   */
  private onConnectivityChange(online: boolean): void {
    if (this.stopped) return;
    if (online) {
      if (this._state === 'open') return;
      if (this.reconnectTimer !== undefined) clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
      this.attempt = 0;
      // Safe to call directly: `openSocket` discards any socket we still hold
      // and refuses to run concurrently with another attempt.
      void this.openSocket();
      return;
    }
    // Going offline: drop the pending retry and say so at once, rather than
    // showing "Reconnecting…" for attempts that cannot succeed.
    if (this.reconnectTimer !== undefined) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.setState('closed');
  }

  connect(): void {
    this.stopped = false;
    this.watchConnectivity(); // re-arm after a `disconnect()`
    this.attempt = 0;
    this.authFailures = 0;
    void this.openSocket();
  }

  disconnect(): void {
    this.stopped = true;
    // Release the window listeners; `connect()` re-arms them.
    this.unsubscribeConnectivity?.();
    this.unsubscribeConnectivity = undefined;
    this.intentionalClose = true;
    if (this.reconnectTimer !== undefined) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.setState('closed');
  }

  send(msg: ClientMessage): boolean {
    if (this._state !== 'open' || !this.ws) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  onMessage(fn: (msg: ServerMessage) => void): () => void {
    this.messageListeners.add(fn);
    return () => this.messageListeners.delete(fn);
  }

  onStateChange(fn: (s: TransportState) => void): () => void {
    this.stateListeners.add(fn);
    return () => this.stateListeners.delete(fn);
  }

  /** The client is too old to write safely — a terminal condition, the app must reload (ADR-0016). */
  onSchemaStale(fn: (required: number) => void): () => void {
    this.schemaStaleListeners.add(fn);
    return () => this.schemaStaleListeners.delete(fn);
  }

  private setState(s: TransportState): void {
    this._state = s;
    for (const fn of this.stateListeners) fn(s);
  }

  /**
   * Tear down whatever socket we currently hold before opening another.
   *
   * Without this, a reconnect could leave the page with *two* live
   * connections: `this.ws = ws` only overwrote the reference, so the previous
   * socket stayed open with all of its listeners still attached. The server
   * registered both, and the consequences were bizarre rather than obviously
   * broken — the client heard lease broadcasts caused by *its own* other
   * connection, concluded someone had taken its lease, re-claimed, was denied
   * by itself (`SET NX` against a key it already held), and every transaction
   * ended up read-only with "you have this open in another tab". Which was
   * technically true and completely useless.
   */
  private discardSocket(): void {
    const ws = this.ws;
    if (!ws) return;
    this.ws = undefined;
    // Suppress the close handler's reconnect: this close is ours.
    try { ws.close(); } catch { /* already closing */ }
  }

  private async openSocket(): Promise<void> {
    if (this.stopped) return;
    // A second concurrent attempt would produce a second connection. This can
    // happen for real: the `online` event fires while a backoff timer is also
    // about to run.
    if (this.connecting) return;
    this.connecting = true;
    try {
      await this.openSocketInner();
    } finally {
      this.connecting = false;
    }
  }

  private async openSocketInner(): Promise<void> {
    if (this.stopped) return;
    this.discardSocket();
    // `fetchToken` is an HTTP round trip. When the browser already knows
    // there is no network, skipping it saves a connection timeout per attempt
    // and keeps the UI's state honest immediately instead of after one.
    if (!this.connectivity.isOnline()) {
      this.setState('closed');
      return;
    }
    this.intentionalClose = false;
    this.setState('connecting');

    let token: string;
    try {
      const result = await this.opts.fetchToken();
      token = result.token;
    } catch (err) {
      // Only a refusal counts. An unreachable network must stay retryable, or
      // going offline once is terminal — see `TokenFetchError`.
      if (err instanceof TokenFetchError && err.fatal) {
        this.authFailures += 1;
        if (this.authFailures >= (this.opts.maxAuthFailures ?? 3)) {
          this.stopped = true;
          this.setState('closed');
          return;
        }
      }
      this.setState('closed');
      this.scheduleReconnect();
      return;
    }

    const WS = this.opts.webSocketCtor ?? WebSocket;
    const ws = new WS(this.opts.wsUrl(), [token]);
    this.ws = ws;

    ws.addEventListener('open', () => {
      if (this.ws !== ws) { try { ws.close(); } catch { /* already closing */ } return; }
      // Sent directly on the raw socket, not via `this.send()`: `send()`
      // gates on `_state === 'open'`, but `_state` only becomes 'open' once
      // `welcome` arrives in response to this very `hello` — going through
      // `send()` here would refuse to send it, and the connection would
      // deadlock in 'connecting' forever.
      ws.send(JSON.stringify({ t: 'hello', schemaVersion: this.opts.schemaVersion, node: this.opts.node }));
    });

    ws.addEventListener('message', (event: MessageEvent) => {
      // A superseded socket may still deliver frames. Ignoring them is what
      // stops a stale connection from driving this state machine — see
      // `discardSocket`.
      if (this.ws !== ws) return;
      const msg = JSON.parse(String(event.data)) as ServerMessage;
      if (msg.t === 'welcome') {
        this.attempt = 0;
        this.authFailures = 0;
        this.setState('open');
      }
      if (msg.t === 'schemaStale') {
        this.stopped = true;
        this.intentionalClose = true;
        for (const fn of this.schemaStaleListeners) fn(msg.required);
      }
      for (const fn of this.messageListeners) fn(msg);
    });

    ws.addEventListener('close', () => {
      // Only the *current* socket closing means we lost the connection; a
      // discarded one closing is expected and must not trigger a reconnect.
      if (this.ws !== ws) return;
      this.ws = undefined;
      if (!this.intentionalClose) this.scheduleReconnect();
    });
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    // Known-offline: no timer at all. The browser's `online` event is what
    // wakes us, which is both faster and cheaper than polling a network that
    // is definitively absent. (`isOnline() === true` proves nothing, so the
    // normal backoff still applies in that case — see `ConnectivityLike`.)
    if (!this.connectivity.isOnline()) {
      this.setState('closed');
      return;
    }
    this.setState('connecting');
    const delay = computeBackoff(this.attempt, this.opts.backoff, this.opts.random);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => { void this.openSocket(); }, delay);
  }
}
